// Shared SR bulk-action state + handlers (Q10 — DESIGN-DECISIONS-2026-05-01).
// Used by SrListPage and FilteredSrListPage. Holds the confirm/reassign modal
// state and the actual API loop runners; the page renders the modals and the
// bulk bar via render props.

import { useCallback, useState } from "react";
import type { JSX } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { CarerPickerDialog } from "../components/CarerPickerDialog.js";
import { bulkPatch, reportBulkResult } from "../pages/SrListPage.js";

interface SrLike { _id: string }

export interface SrBulkActionsApi {
  askCancel: (ids: readonly string[]) => void;
  askReassign: (ids: readonly string[]) => void;
}

interface Args<T extends SrLike> {
  rows: readonly T[];
  refetch: () => Promise<unknown>;
  clearSelection: () => void;
  refLabel?: (r: T) => string;
}

export interface SrBulkActionsResult {
  api: SrBulkActionsApi;
  modals: JSX.Element | null;
}

export function useSrBulkActions<T extends SrLike>(args: Args<T>): SrBulkActionsResult {
  const { rows, refetch, clearSelection, refLabel } = args;
  const [confirmCancel, setConfirmCancel] = useState<readonly string[] | null>(null);
  const [reassignFor, setReassignFor] = useState<readonly string[] | null>(null);
  const [busy, setBusy] = useState(false);

  const askCancel = useCallback((ids: readonly string[]) => setConfirmCancel(ids), []);
  const askReassign = useCallback((ids: readonly string[]) => setReassignFor(ids), []);

  async function runCancel(ids: readonly string[]): Promise<void> {
    setBusy(true);
    try {
      const { ok, failed } = await bulkPatch(ids, (id) => `/sr/${id}/status`, { status: "cancelled" });
      reportBulkResult("Cancelled", ok, failed);
      clearSelection();
      await refetch();
    } finally { setBusy(false); setConfirmCancel(null); }
  }

  async function runReassign(ids: readonly string[], carerId: string): Promise<void> {
    setBusy(true);
    try {
      const { ok, failed } = await bulkPatch(ids, (id) => `/sr/${id}/assign`, { carer_id: carerId });
      reportBulkResult("Reassigned", ok, failed);
      clearSelection();
      await refetch();
    } finally { setBusy(false); setReassignFor(null); }
  }

  const modals = (confirmCancel || reassignFor) ? (
    <>
      {confirmCancel && (
        <ConfirmDialog
          title="Cancel selected service requests?"
          destructive
          busy={busy}
          confirmLabel={`Cancel ${confirmCancel.length} SR${confirmCancel.length === 1 ? "" : "s"}`}
          message={
            <>
              <p>This will set <strong>{confirmCancel.length}</strong> service request{confirmCancel.length === 1 ? "" : "s"} to <em>cancelled</em>.</p>
              {refLabel && (
                <ul className="text-xs text-slate-500 font-mono max-h-32 overflow-y-auto border border-slate-100 rounded p-2 space-y-0.5">
                  {confirmCancel.slice(0, 20).map((id) => {
                    const row = rows.find((r) => r._id === id);
                    return <li key={id}>{row ? refLabel(row) : id.slice(-8)}</li>;
                  })}
                  {confirmCancel.length > 20 && <li className="text-slate-400">… and {confirmCancel.length - 20} more</li>}
                </ul>
              )}
              <p className="text-xs text-red-600">This action cannot be undone.</p>
            </>
          }
          onClose={() => setConfirmCancel(null)}
          onConfirm={() => { void runCancel(confirmCancel); }}
        />
      )}
      {reassignFor && (
        <CarerPickerDialog
          affectedCount={reassignFor.length}
          busy={busy}
          onClose={() => setReassignFor(null)}
          onConfirm={(carerId) => { void runReassign(reassignFor, carerId); }}
        />
      )}
    </>
  ) : null;

  return { api: { askCancel, askReassign }, modals };
}
